import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "../styles.css";

interface Props {
    titleGraph: string;
    data?: any[];
    axisLabels?: string[];
    fullWidth?: boolean;
    exportPDF?: boolean;
}

const LINE_COLORS = [
    "#57CE71",
    "#5AC8FA",
    "#FF9501",
    "#FF3B30",
    "#5856D6",
    "#8A2BE2",
    "#FFCC00",
    "#34C759",
    "#007AFF",
    "#FF2D55",
    "#2737FA",
];

const ChartMultiLine: React.FC<Props> = ({
    titleGraph,
    data,
    axisLabels,
    fullWidth,
    exportPDF,
}) => {
    Chart.register(ChartDataLabels);
    Chart.defaults.font.size = 12;

    const labels = useMemo(() => data?.map((el: any) => el?.label) ?? [], [data]);

    const microAreaInitials = useMemo(() => {
        if (!data) return [];

        const initialsSet = new Set<string>();
        data.forEach((day: any) => {
            day?.info?.forEach((microArea: any) => {
                if (microArea?.initials) {
                    initialsSet.add(microArea.initials);
                }
            });
        });

        return Array.from(initialsSet).sort((a, b) => a.localeCompare(b));
    }, [data]);

    const datasets = useMemo(() => {
        if (!data) return [];

        return microAreaInitials.map((initials, index) => {
            const color = LINE_COLORS[index % LINE_COLORS.length];

            return {
                label: initials,
                data: data.map((day: any) => {
                    const microArea = day?.info?.find(
                        (info: any) => info?.initials === initials
                    );

                    if (microArea === undefined || microArea.value === undefined || microArea.value === null) {
                        return null;
                    }

                    return Number(microArea.value);
                }),
                borderColor: color,
                backgroundColor: color,
                pointBackgroundColor: color,
                pointBorderColor: color,
                fill: false,
                tension: 0.25,
                spanGaps: true,
                pointRadius: 3,
                pointHoverRadius: 4,
            };
        });
    }, [data, microAreaInitials]);

    const hasData =
        datasets.length > 0 &&
        datasets.some((dataset: any) =>
            dataset.data.some((value: number | null) => value !== null && value !== 0)
        );

    if (!data || data.length === 0) {
        return <></>;
    }

    return (
        <div>
            <span className="titleGraph">{titleGraph}</span>
            <div
                className={`contentGraphBorder ${
                    fullWidth ? `containerGraphFull${exportPDF ? "-print" : ""}` : ""
                }`}
            >
                {!hasData ? (
                    <div className="noData">
                        <span>Sem dados</span>
                    </div>
                ) : (
                    <Line
                        className={`${fullWidth ? "fullWidth" : ""}`}
                        data={{
                            labels,
                            datasets,
                        }}
                        options={{
                            devicePixelRatio: 4,
                            animation: {
                                duration: 0,
                            },
                            plugins: {
                                legend: {
                                    display: true,
                                    position: "bottom",
                                    labels: {
                                        boxWidth: 10,
                                    },
                                },
                                datalabels: {
                                    display: false,
                                },
                                tooltip: {
                                    enabled: true,
                                    mode: "nearest",
                                    intersect: false,
                                },
                            },
                            layout: {
                                padding: {
                                    top: 30,
                                    right: 2,
                                },
                            },
                            interaction: {
                                mode: "index",
                                intersect: false,
                            },
                            scales: {
                                x: {
                                    grid: {
                                        display: false,
                                    },
                                    title: {
                                        display: !!axisLabels,
                                        text: axisLabels ? axisLabels[0] : "",
                                        font: {
                                            size: 12,
                                        },
                                    },
                                },
                                y: {
                                    title: {
                                        display: !!axisLabels,
                                        text: axisLabels ? axisLabels[1] : "",
                                        font: {
                                            size: 12,
                                        },
                                    },
                                },
                            },
                        }}
                        width={fullWidth ? 1360 : 285}
                        height={fullWidth ? 500 : 285}
                    />
                )}
            </div>
        </div>
    );
};

export default ChartMultiLine;


